import { useState, useEffect } from 'react'
import { getParsedQuery } from 'utils/common'
import { useRouter } from 'next/router'
import fetchCities from 'app/SeFriendly/fetchCities'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import { CounselingTypeEnum } from 'app/Bookings/constants'
import { slugifyCity } from 'app/ProviderSearch/utils'
import { specialtyValueMapping } from 'app/SeFriendly/constants'

const useCanonicalLink = (groupedFilters: GroupedProviderFilter): string => {
  const [canonicalLink, setCanonicalLink] = useState<string>('')
  const router = useRouter()

  // Override canonical for SE friendly page matches
  useEffect(() => {
    const overrideCanonical = async (): Promise<void> => {
      const qsQuery = getParsedQuery(router)
      const {
        state = '',
        location = '',
        counselingType: activeCounselingTypeIds = [],
        areaOfConcern: activeAreaOfConcernFilterIds = [],
        counselingApproach: activeCounselingApproachFilterIds = [],
      } = qsQuery

      const baseUrl = window.location.origin
      const defaultCanonicalUrl = `${baseUrl}/therapists`

      // Fetch all SE friendly cities from ButterCMS
      const allSeFriendlyCities = await fetchCities()

      // Determine if current city is a valid SE friendly city
      const validSeFriendlyCity = allSeFriendlyCities.find(
        (city) =>
          location.toLowerCase().includes(city.city.toLowerCase()) &&
          city.state.toLowerCase() === state.toLowerCase()
      )

      // Ignore override if the city does not match a valid SE friendly city
      if (!validSeFriendlyCity) {
        setCanonicalLink(defaultCanonicalUrl)
        return
      }

      // Collect available filters
      const allCounselingTypeFilters: ProviderFilter[] =
        groupedFilters[ProviderFilterTypes.COUNSELING_TYPE]
      const allAreaOfConcernFilters: ProviderFilter[] =
        groupedFilters[ProviderFilterTypes.AREA_OF_CONCERN]
      const allCounselingApproachFilters: ProviderFilter[] =
        groupedFilters[ProviderFilterTypes.COUNSELING_APPROACH]

      // Determine if counseling type is psychiatrists or therapists
      const getCounselingType = (): 'psychiatrists' | 'therapists' => {
        const psychiatryCounselingTypeId = allCounselingTypeFilters.find(
          (filter: ProviderFilter) =>
            filter.value === CounselingTypeEnum.MEDICATION
        )
        return activeCounselingTypeIds.includes(psychiatryCounselingTypeId?.id)
          ? 'psychiatrists'
          : 'therapists'
      }

      // Handle override when no filters are active for area of concern or counseling approach
      // Override pattern here is /:type/:state/:city
      if (
        !activeAreaOfConcernFilterIds.length &&
        !activeCounselingApproachFilterIds.length
      ) {
        const _type = getCounselingType()
        const _state = state.toLowerCase()
        const _city = slugifyCity(validSeFriendlyCity.city)
        const url = `${baseUrl}/${_type}/${state}/${_city}`
        setCanonicalLink(url)
        return
      }

      // Get active filters by matching the active area of concern and counseling approach filter IDs
      const getActiveFilters = (): ProviderFilter[] => {
        const activeAreaOfConcernFilters = allAreaOfConcernFilters.filter(
          (filter: ProviderFilter) =>
            activeAreaOfConcernFilterIds.includes(filter.id)
        )
        const activeCounselingApproachFilters = allCounselingApproachFilters.filter(
          (filter: ProviderFilter) =>
            activeCounselingApproachFilterIds.includes(filter.id)
        )
        return [
          ...activeCounselingApproachFilters,
          ...activeAreaOfConcernFilters,
        ]
      }

      // Get active filters that are valid SE friendly specialty filters
      const activeFilters = getActiveFilters()
      const activeSeFriendlySpecialtyFilters = activeFilters.filter(
        (activeFilter) =>
          Object.values(specialtyValueMapping).includes(activeFilter.value)
      )

      // Ignore override if there are no active filters that are SE friendly
      if (!activeSeFriendlySpecialtyFilters.length) {
        setCanonicalLink(defaultCanonicalUrl)
        return
      }

      // Handle override canonical (with specialty)
      // Override pattern here is /:type/:state/:city/:specialty
      const _type = getCounselingType()
      const _state = state.toLowerCase()
      const _city = slugifyCity(validSeFriendlyCity.city)
      const specialty = activeSeFriendlySpecialtyFilters[0].value
      const seFriendlySpecialty = Object.keys(specialtyValueMapping).find(
        (key) => specialtyValueMapping[key] === specialty
      )
      const url = `${baseUrl}/${_type}/${state}/${_city}/${seFriendlySpecialty}`
      setCanonicalLink(url)
    }

    // Run async fn in useEffect
    overrideCanonical()
  }, [])

  return canonicalLink
}

export default useCanonicalLink
